import { useEffect, useRef, useState, useCallback } from "react";
import { PageGuide } from "@/components/PageGuide";
import { CrewBadges, useCrewBadges } from "@/components/Badges";
import { useSearchParams, Link } from "react-router-dom";
import { api, apiError, API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useRoomSocket } from "@/hooks/useRoomSocket";
import { useCall } from "@/hooks/useCall";
import { CallPanel } from "@/components/CallPanel";
import { CallHistory } from "@/components/CallHistory";
import { LanguageSelect } from "@/components/LanguageSelect";
import { Flag } from "@/components/Flag";
import { ThemePicker } from "@/components/calendar/ThemePicker";
import { RoomInvites } from "@/components/messages/RoomInvites";
import { RichText } from "@/components/messages/RichText";
import { LinkPreview } from "@/components/messages/LinkPreview";
import { ListenBoard } from "@/components/news/ListenBoard";
import { useAccent } from "@/context/AccentContext";
import { encryptText, decryptText, savedPassphrase, unlockRoom } from "@/lib/crypto";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Plus, Send, Users, Copy, Lightbulb, Eye, EyeOff, Mic, Square, Volume2, Loader2,
  Phone, Video, Lock, Timer, KeyRound, FileText, BookmarkPlus, Check,
  Pin, PinOff, Reply, Volume1, VolumeX, X, Trash2, Pencil, PhoneCall, Smile, Star, Search,
  ChevronUp, ChevronDown, Headphones, StickyNote, ArrowDown, ThumbsUp,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

// a broad set so most chats can find a fitting mark
const ROOM_MARKS = [
  "❤️", "🏠", "👨‍👩‍👧", "💼", "⚽", "✈️", "🎉", "🍜", "🎵", "📚",
  "🐶", "☀️", "⭐", "🔥", "💡", "📌", "🗓️", "⏰", "💰", "🛒",
  "🎓", "🏥", "💊", "🚗", "🚲", "🏖️", "🏔️", "🌍", "🌸", "🐱",
  "☕", "🍕", "🍺", "🎬", "🎮", "🎨", "📷", "🎤", "🏀", "🎾",
  "🏋️", "🧘", "🙏", "💬", "📞", "🔧", "🏢", "🧾", "🎁", "🚀",
  "🌙", "🌈", "❄️", "🍀", "🌊", "🌴", "🍎", "🍞", "🍰", "🍫",
  "🍷", "🥂", "🧊", "🥗", "🍣", "🌮", "🥘", "🧁", "🎂", "🍦",
  "🐟", "🐦", "🐴", "🐝", "🦋", "🌻", "🌵", "🍁", "⛰️", "🏝️",
  "🚌", "🚆", "🛵", "⛵", "🏍️", "🧳", "🗺️", "🧭", "🎯", "🏆",
  "🥇", "🎲", "🃏", "🎸", "🥁", "🎻", "📺", "📰", "✏️", "🔑",
];


const dayLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const midnight = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((midnight(today) - midnight(d)) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return d.toLocaleDateString([], { weekday: "long" });
  return d.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
};

const TTL_OPTIONS = [
  { v: 0, label: "Off" },
  { v: 3600, label: "1 hour" },
  { v: 86400, label: "24 hours" },
  { v: 604800, label: "7 days" },
];

export default function TransMessages() {
  const { t, lang, languages } = useI18n();
  const { theme, accent, saveTheme: persistTheme } = useAccent();
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const crew = useCrewBadges([
    ...(active?.member_details || []).map((m) => m.user_id),
    ...messages.map((m) => m.sender_id),
  ]);

  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [newRoomMode, setNewRoomMode] = useState("standard");
  const [newRoomTtl, setNewRoomTtl] = useState(0);
  const [sending, setSending] = useState(false);
  const [showOriginal, setShowOriginal] = useState(true);
  const [tip, setTip] = useState(null);
  const [roomLang, setRoomLang] = useState(null);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [locked, setLocked] = useState(false);
  const [passphrase, setPassphrase] = useState("");
  const [callOpen, setCallOpen] = useState(false);
  const [reads, setReads] = useState([]);
  const [typing, setTyping] = useState({});
  const [transcripts, setTranscripts] = useState([]);
  const [openTranscript, setOpenTranscript] = useState(null);
  const [pins, setPins] = useState([]);
  const [pinNote, setPinNote] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [autoPlay, setAutoPlay] = useState(localStorage.getItem("cml_autoplay") !== "off");
  const [confirmRoom, setConfirmRoom] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState("");
  const [listRename, setListRename] = useState(null);  // a room being renamed from the list
  const [marking, setMarking] = useState("");          // a room whose little icon is being picked
  const [chatFilter, setChatFilter] = useState("all");
  const [byIcon, setByIcon] = useState(localStorage.getItem("cml_chats_by_icon") === "1");
  const [iconOnly, setIconOnly] = useState("");
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState({ rooms: [], messages: [] });
  const [jumpedId, setJumpedId] = useState("");
  const [findOpen, setFindOpen] = useState(false);
  const [findTerm, setFindTerm] = useState("");
  const [findAt, setFindAt] = useState(0);
  const [starred, setStarred] = useState(new Set());
  const [starList, setStarList] = useState(null);   // null = panel closed
  const [starFolders, setStarFolders] = useState([]);
  const [starFolder, setStarFolder] = useState("");  // "" = everything
  const [passOn, setPassOn] = useState("");
  const [passLang, setPassLang] = useState("en");
  const [passRoom, setPassRoom] = useState("");
  const [passing, setPassing] = useState(false);
  const [showBoard, setShowBoard] = useState(false);
  const [recentMarks, setRecentMarks] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("cml_recent_marks") || "[]");
    } catch {
      return [];
    }
  });
  const newRoomRef = useRef(null);
  const [showMembers, setShowMembers] = useState(false);
  const [unseen, setUnseen] = useState(0);
  const [away, setAway] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [emailReady, setEmailReady] = useState(false);
  const [pendingCall, setPendingCall] = useState(null);
  const [params, setParams] = useSearchParams();
  const endRef = useRef(null);
  const scrollBoxRef = useRef(null);
  const lastSeenRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const activeRef = useRef(null);
  const lastTypingRef = useRef(0);
  const playedRef = useRef(new Set());

  const isPrivate = active?.mode === "private";

  const loadRooms = useCallback(async () => {
    const { data } = await api.get("/rooms");
    setRooms(data);
    const current = activeRef.current;
    if (current) {
      const fresh = data.find((r) => r.room_id === current.room_id);
      if (fresh) {
        setActive(fresh);
        activeRef.current = fresh;
      }
    }
    return data;
  }, []);

  const loadMessages = useCallback(async (room) => {
    const { data } = await api.get(`/rooms/${room.room_id}/messages`);
    setRoomLang(data.my_language);
    setReads(data.reads || []);
    setStarred(new Set(data.starred || []));
    let list = data.messages;
    if (data.mode === "private") {
      list = await Promise.all(
        data.messages.map(async (m) => ({
          ...m,
          display_text: await decryptText(room.room_id, m.display_text),
        }))
      );
      setLocked(!savedPassphrase(room.room_id));
    } else {
      setLocked(false);
    }
    setMessages(list);
    const last = list[list.length - 1];
    if (last) {
      api
        .post(`/rooms/${room.room_id}/read`, { message_id: last.message_id })
        .then(() =>
          setRooms((rs) => rs.map((r) => (r.room_id === room.room_id ? { ...r, unread: 0 } : r))))
        .catch(() => {});
    }
  }, []);

  const loadTranscripts = useCallback(async (room) => {
    const { data } = await api.get(`/rooms/${room.room_id}/calls`);
    setTranscripts(data);
  }, []);

  const loadPins = useCallback(async (room) => {
    const { data } = await api.get(`/rooms/${room.room_id}/pins`);
    setPins(data);
  }, []);

  const pinMessage = async (payload) => {
    try {
      await api.post(`/rooms/${active.room_id}/pins`, payload);
      await loadPins(active);
      setPinNote("");
      toast.success("Pinned to the top of this room");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const unpin = async (pinId) => {
    await api.delete(`/rooms/${active.room_id}/pins/${pinId}`);
    loadPins(active);
  };


  const saveName = async () => {
    const name = newName.trim();
    setRenaming(false);
    if (!name || name === active.name) return;
    try {
      await api.patch(`/rooms/${active.room_id}`, { name });
      setActive((prev) => ({ ...prev, name }));
      loadRooms();
      toast.success("Room renamed");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveListName = async () => {
    if (!listRename) return;
    const { room_id, name: typed } = listRename;
    const name = typed.trim();
    setListRename(null);
    const was = rooms.find((r) => r.room_id === room_id)?.name;
    if (!name || name === was) return;
    try {
      await api.patch(`/rooms/${room_id}`, { name });
      setRooms((rs) => rs.map((r) => (r.room_id === room_id ? { ...r, name } : r)));
      setActive((prev) => (prev?.room_id === room_id ? { ...prev, name } : prev));
      toast.success("Room renamed");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setHits({ rooms: [], messages: [] });
      return;
    }
    const timer = setTimeout(() => {
      api.get(`/rooms/search?q=${encodeURIComponent(term)}`)
        .then(({ data }) => setHits(data))
        .catch(() => setHits({ rooms: [], messages: [] }));
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!jumpedId || messages.length === 0) return;
    const at = setTimeout(() => {
      document.getElementById(`msg-${jumpedId}`)
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 450);
    const off = setTimeout(() => setJumpedId(""), 5000);
    return () => {
      clearTimeout(at);
      clearTimeout(off);
    };
  }, [jumpedId, messages.length]);

  const findFor = findTerm.trim().toLowerCase();
  const found = findFor.length < 2 ? [] : messages.filter((m) => (
    `${m.display_text || ""} ${m.original_text || ""}`.toLowerCase().includes(findFor)));

  const hopFind = (step) => {
    if (found.length === 0) return;
    const at = (findAt + step + found.length) % found.length;
    setFindAt(at);
    setJumpedId(found[at].message_id);
  };

  useEffect(() => {
    if (found.length === 0) return;
    const last = found.length - 1;
    setFindAt(last);
    setJumpedId(found[last].message_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findFor]);

  const toggleStar = async (m) => {
    const on = !starred.has(m.message_id);
    setStarred((s) => {
      const next = new Set(s);
      if (on) next.add(m.message_id); else next.delete(m.message_id);
      return next;
    });
    try {
      await api.post(`/rooms/${active.room_id}/messages/${m.message_id}/star`, { on });
      if (starList) openStarred();
      toast.success(on ? "Starred — find it under Starred" : "Star removed");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not star that");
    }
  };

  const passItOn = async (s) => {
    setPassing(true);
    try {
      const { data } = await api.post(`/messages/starred/${s.message_id}/send`,
                                      { lang: passLang, room_id: passRoom });
      if (data.sent_to) {
        toast.success(`Sent on to ${data.sent_to}`);
        if (active?.room_id === passRoom) loadMessages(active);
      } else {
        try {
          await navigator.clipboard.writeText(data.text);
          toast.success("Copied in their language — paste it anywhere");
        } catch {
          toast.success(data.text);
        }
      }
      setPassOn("");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send that on");
    } finally {
      setPassing(false);
    }
  };

  const openStarred = async () => {
    try {
      const { data } = await api.get("/messages/starred");
      setStarList(data.rows);
      setStarFolders(data.folders || []);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not load your starred messages");
    }
  };

  const noteStarred = async (messageId, note) => {
    setStarList((rows) => rows.map((r) => (
      r.message_id === messageId ? { ...r, note } : r)));
    try {
      await api.put(`/messages/starred/${messageId}/note`, { note });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not keep that line");
    }
  };

  const fileStarred = async (messageId, folder) => {
    setStarList((rows) => rows.map((r) => (
      r.message_id === messageId ? { ...r, folder } : r)));
    try {
      await api.put(`/messages/starred/${messageId}/folder`, { folder });
      const { data } = await api.get("/messages/starred");
      setStarFolders(data.folders || []);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not file that");
    }
  };

  const toggleFavourite = async (roomId, on) => {
    try {
      await api.put(`/rooms/${roomId}/favourite`, { on });
      setRooms((rs) => rs.map((r) => (r.room_id === roomId ? { ...r, my_favourite: on } : r)));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that");
    }
  };

  const unreadRooms = rooms.filter((r) => r.unread > 0).length;
  const asked = query.trim().toLowerCase();
  const shownRooms = rooms.filter((r) => (
    chatFilter === "unread" ? r.unread > 0
      : chatFilter === "fav" ? r.my_favourite
      : chatFilter === "groups" ? (r.members || []).length > 2
      : true
  )).filter((r) => (iconOnly ? (r.my_emoji || "") === iconOnly : true))
    .filter((r) => (asked
      ? r.name.toLowerCase().includes(asked) || hits.messages.some((m) => m.room_id === r.room_id)
      : true));

  // favourites are pinned to the top, then either one plain list or a section per icon
  const listItems = (() => {
    const pinnedFirst = [...shownRooms].sort(
      (a, b) => (b.my_favourite ? 1 : 0) - (a.my_favourite ? 1 : 0));
    const out = [];
    if (byIcon) {
      const pots = new Map();
      pinnedFirst.forEach((r) => {
        const mark = r.my_emoji || "";
        if (!pots.has(mark)) pots.set(mark, []);
        pots.get(mark).push(r);
      });
      [...pots.entries()]
        .sort(([a], [b]) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)))
        .forEach(([mark, rows]) => {
          out.push({ type: "head", key: `h-${mark || "none"}`, slug: mark || "none",
                     label: mark ? `${mark}  ${rows.length}` : `No icon · ${rows.length}` });
          rows.forEach((r) => out.push({ type: "room", r }));
        });
      return out;
    }
    const pinned = pinnedFirst.filter((r) => r.my_favourite);
    const rest = pinnedFirst.filter((r) => !r.my_favourite);
    if (pinned.length && rest.length) {
      out.push({ type: "head", key: "h-pinned", slug: "pinned", label: "Pinned" });
      pinned.forEach((r) => out.push({ type: "room", r }));
      out.push({ type: "head", key: "h-rest", slug: "rest", label: "Everything else" });
      rest.forEach((r) => out.push({ type: "room", r }));
      return out;
    }
    return pinnedFirst.map((r) => ({ type: "room", r }));
  })();

  const setRoomMark = async (roomId, emoji) => {
    setMarking("");
    if (emoji) {
      const kept = [emoji, ...recentMarks.filter((m) => m !== emoji)].slice(0, 10);
      setRecentMarks(kept);
      localStorage.setItem("cml_recent_marks", JSON.stringify(kept));
    }
    try {
      await api.put(`/rooms/${roomId}/emoji`, { emoji });
      setRooms((rs) => rs.map((r) => (r.room_id === roomId ? { ...r, my_emoji: emoji } : r)));
      setActive((prev) => (prev?.room_id === roomId ? { ...prev, my_emoji: emoji } : prev));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that icon");
    }
  };

  const removeMember = async (memberId, name) => {
    try {
      await api.delete(`/rooms/${active.room_id}/members/${memberId}`);
      const list = await loadRooms();
      const fresh = list.find((r) => r.room_id === active.room_id);
      if (fresh) setActive(fresh);
      toast.success(`${name || "They"} can no longer see this room`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const removeRoom = async () => {
    const room = confirmRoom;
    setConfirmRoom(null);
    try {
      const { data } = await api.delete(`/rooms/${room.room_id}`);
      toast.success(data.deleted ? `“${room.name}” deleted` : `You left “${room.name}”`);
      if (active?.room_id === room.room_id) setActive(null);
      loadRooms();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openRoom = (room) => {
    setActive(room);
    activeRef.current = room;
    lastSeenRef.current = null;
    setUnseen(0);
    setTip(null);
    setCallOpen(false);
    setPassphrase("");
    setOpenTranscript(null);
    setTyping({});
    setReplyTo(null);
    loadMessages(room);
    loadTranscripts(room);
    loadPins(room);
  };

  const handleSocketEvent = useCallback(
    (evt) => {
      const room = activeRef.current;
      if (evt.type === "message" || evt.type === "settings") {
        if (evt.from_id)
          setTyping(({ [evt.from_id]: _gone, ...rest }) => rest);
        if (room) loadMessages(room);
        loadRooms();
      } else if (evt.type === "read" && room) {
        api.get(`/rooms/${room.room_id}/messages`).then(({ data }) => setReads(data.reads || []));
      } else if (evt.type === "typing") {
        setTyping((prev) => ({ ...prev, [evt.from_id]: { name: evt.from_name, at: Date.now() } }));
      } else if (evt.type === "call-leave" && room) {
        loadTranscripts(room);
        loadRooms();
      } else if (evt.type === "call-started" || evt.type === "call-ended") {
        loadRooms();
        if (room) loadTranscripts(room);
      } else if (evt.type === "call-declined") {
        if (evt.by) toast.info(`${evt.by} can't take the call right now`);
      } else if (evt.type === "pin" && room) {
        loadPins(room);
      }
      callRef.current?.handleEvent(evt);
    },
    [loadMessages, loadRooms, loadTranscripts, loadPins]
  );

  const { send: wsSend, socketRef } = useRoomSocket(active?.room_id, handleSocketEvent);
  const call = useCall({ roomId: active?.room_id, send: wsSend, myLang: roomLang || lang });
  const callRef = useRef(call);
  callRef.current = call;

  useEffect(() => {
    const wanted = params.get("room");
    loadRooms().then((list) => {
      if (list.length && !wanted) openRoom(list[0]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveTheme = async (next) => {
    try {
      await persistTheme(next);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that colour");
    }
  };

  // Rings and hang-ups arrive on the personal socket — keep the room list in sync.
  useEffect(() => {
    const onCallEvent = () => loadRooms();
    window.addEventListener("chatmilu:callevent", onCallEvent);
    return () => window.removeEventListener("chatmilu:callevent", onCallEvent);
  }, []);

  // Accepting a ring lands here as /messages?room=…&call=voice|video.
  useEffect(() => {
    const roomId = params.get("room");
    const kind = params.get("call");
    if (!roomId) return;
    (async () => {
      const list = await loadRooms();
      const room = list.find((r) => r.room_id === roomId);
      if (room) {
        openRoom(room);
        if (kind) setPendingCall(kind === "video");
      }
      setParams({}, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Wait for the room socket before joining, otherwise the signaling handshake is lost.
  useEffect(() => {
    if (pendingCall === null || !active) return;
    let tries = 0;
    const iv = setInterval(() => {
      tries += 1;
      if (socketRef.current?.readyState === 1) {
        clearInterval(iv);
        setPendingCall(null);
        startCall(pendingCall);
      } else if (tries > 24) {
        clearInterval(iv);
        setPendingCall(null);
        toast.error("Could not connect to the call — try again");
      }
    }, 250);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCall, active?.room_id]);

  // Polling safety net in case the websocket is unavailable.
  useEffect(() => {
    if (!active?.room_id) return;
    const poll = setInterval(() => {
      const room = activeRef.current;
      if (room) loadMessages(room);
    }, 15000);
    const prune = setInterval(
      () =>
        setTyping((prev) =>
          Object.fromEntries(Object.entries(prev).filter(([, v]) => Date.now() - v.at < 4000))
        ),
      1500
    );
    return () => {
      clearInterval(poll);
      clearInterval(prune);
    };
  }, [active?.room_id, lang, loadMessages]);

  const notifyTyping = () => {
    if (Date.now() - lastTypingRef.current < 1800) return;
    lastTypingRef.current = Date.now();
    wsSend({ type: "typing" });
  };

  const savePhrase = async (text, language) => {
    try {
      await api.post("/phrasebook", { text, language, source: "chat" });
      toast.success("Saved to your phrasebook");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  // The thread grows with the page, so "am I at the bottom" may mean the window, not the box.
  const atBottom = useCallback(() => {
    const box = scrollBoxRef.current;
    if (box && box.scrollHeight - box.clientHeight > 40)
      return box.scrollHeight - box.scrollTop - box.clientHeight < 140;
    return window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 220;
  }, []);

  // Never yank the view around: only follow new messages when already at the bottom.
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last?.message_id || last.message_id === lastSeenRef.current) return;
    const firstPaint = lastSeenRef.current === null;
    lastSeenRef.current = last.message_id;
    const mine = last.sender_id === user?.user_id;
    if (firstPaint || mine || atBottom()) {
      setUnseen(0);
      endRef.current?.scrollIntoView({ behavior: firstPaint ? "auto" : "smooth" });
    } else {
      setUnseen((n) => n + 1);
    }
  }, [messages, user?.user_id, atBottom]);

  useEffect(() => {
    const onScroll = () => {
      const home = atBottom();
      setAway(!home);
      if (home) setUnseen(0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    scrollBoxRef.current?.addEventListener("scroll", onScroll, { passive: true });
    const box = scrollBoxRef.current;
    return () => {
      window.removeEventListener("scroll", onScroll);
      box?.removeEventListener("scroll", onScroll);
    };
  }, [atBottom, active?.room_id]);

  // Typing flags go stale the moment someone stops — drop them after 4s.
  useEffect(() => {
    if (!Object.keys(typing).length) return;
    const id = setInterval(() => {
      setTyping((prev) => {
        const fresh = Object.fromEntries(
          Object.entries(prev).filter(([, v]) => Date.now() - v.at < 4000),
        );
        return Object.keys(fresh).length === Object.keys(prev).length ? prev : fresh;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [typing]);

  const typingNames = Object.entries(typing)
    .filter(([id]) => id !== user?.user_id)
    .map(([, v]) => v.name || "Someone");

  const cheerBack = async (m) => {
    try {
      await api.post(`/rooms/${active.room_id}/messages`, {
        text: `👍 ${m.sender_name || "You"} — right back at you, let's both keep it going!`,
        kind: "cheer",
      });
      toast.success("Cheered back");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not cheer back");
    }
  };

  const saveEdit = async (m) => {
    const text = editText.trim();
    setEditingId(null);
    if (!text || text === m.original_text) return;
    try {
      await api.patch(`/rooms/${active.room_id}/messages/${m.message_id}`, { text });
      loadMessages(active);
      toast.success("Message updated for everyone");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const deleteMessage = async (m) => {
    const room = active;
    try {
      const { data } = await api.delete(`/rooms/${room.room_id}/messages/${m.message_id}`);
      setMessages((prev) => prev.filter((x) => x.message_id !== m.message_id));
      loadRooms();
      toast.success("Message deleted", {
        duration: (data.undo_seconds || 8) * 1000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/rooms/${room.room_id}/messages/${m.message_id}/restore`);
              loadMessages(room);
              loadRooms();
              toast.success("Message restored");
            } catch (err) {
              toast.error(apiError(err.response?.data?.detail));
            }
          },
        },
      });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const jumpToLatest = () => {
    setUnseen(0);
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Auto-play newly arrived voice messages in my language.
  useEffect(() => {
    if (!autoPlay || !active || messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (
      last.kind === "voice" &&
      last.sender_id !== user?.user_id &&
      !last.encrypted &&
      !playedRef.current.has(last.message_id)
    ) {
      playedRef.current.add(last.message_id);
      playMessage(last);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, autoPlay]);

  useEffect(() => {
    api.get("/email/status").then(({ data }) => setEmailReady(data.configured)).catch(() => {});
  }, []);

  const createRoom = async () => {
    if (!name.trim()) return;
    try {
      const { data } = await api.post("/rooms", {
        name: name.trim(), is_group: true, mode: newRoomMode, ttl_seconds: newRoomTtl,
      });
      setName("");
      const list = await loadRooms();
      const room = list.find((r) => r.room_id === data.room_id) || data;
      openRoom(room);
      toast.success(
        newRoomMode === "private"
          ? `Private room created · code ${data.invite_code} · set a passphrase to start`
          : `Room created · invite code ${data.invite_code}`
      );
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const unlock = async () => {
    if (!passphrase.trim() || !active) return;
    await unlockRoom(active.room_id, passphrase.trim());
    setPassphrase("");
    await loadMessages(active);
    toast.success("Room unlocked on this device");
  };

  const changeTtl = async (seconds) => {
    if (!active) return;
    try {
      await api.put(`/rooms/${active.room_id}/settings`, { ttl_seconds: Number(seconds) });
      const list = await loadRooms();
      const room = list.find((r) => r.room_id === active.room_id);
      if (room) {
        setActive(room);
        activeRef.current = room;
      }
      toast.success("Self-destruct timer updated");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const startCall = async (withVideo) => {
    const ok = await call.start(withVideo);
    if (!ok) return toast.error("Camera / microphone permission denied");
    setCallOpen(true);
  };

  const joinRoom = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.post("/rooms/join", { invite_code: code.trim() });
      setCode("");
      const list = await loadRooms();
      const room = list.find((r) => r.room_id === data.room_id);
      if (room) openRoom(room);
      toast.success("Joined room");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const send = async () => {
    if (!text.trim() || !active || sending) return;
    setSending(true);
    try {
      const body = isPrivate ? await encryptText(active.room_id, text.trim()) : text.trim();
      await api.post(`/rooms/${active.room_id}/messages`, { text: body });
      setText("");
      await loadMessages(active);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const getTip = async () => {
    if (!active) return;
    const { data } = await api.get(`/rooms/${active.room_id}/tip`);
    setTip(data.tip || "Send a message first to get a learning tip.");
  };

  const changeRoomLanguage = async (code) => {
    if (!active) return;
    setRoomLang(code);
    await api.put(`/rooms/${active.room_id}/preference`, { language: code });
    await loadMessages(active);
    await loadRooms();
    toast.success(languages.find((l) => l.code === code)?.native || code);
  };

  const startRecording = async () => {
    if (!active) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 1200) return toast.error("Recording too short");
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "voice.webm");
          if (replyTo) fd.append("reply_to", replyTo.message_id);
          await api.post(`/rooms/${active.room_id}/voice`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setReplyTo(null);
          await loadMessages(active);
        } catch (e) {
          toast.error(apiError(e.response?.data?.detail));
        } finally {
          setUploading(false);
        }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Microphone permission denied");
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    recorderRef.current = null;
    setRecording(false);
  };

  const playMessage = async (m) => {
    setPlayingId(m.message_id);
    try {
      const res = await api.get(`/rooms/${active.room_id}/messages/${m.message_id}/speech`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audio.onended = () => setPlayingId(null);
      await audio.play();
    } catch {
      toast.error("Could not play this message");
      setPlayingId(null);
    }
  };

  return (
    <div className="cml-theme mx-auto grid max-w-7xl gap-6 px-5 py-8 sm:px-8 lg:grid-cols-12"
         style={{ "--theme": accent }} data-testid="transmessages-page">
      <aside className="space-y-5 lg:col-span-4">
        <ThemePicker
          theme={theme}
          onPick={saveTheme}
          label="Messages colour"
          testId="messages-theme"
          note="follows you across ChatMiLu"
        />
        <div className="rounded-3xl border border-white/10 bg-panel p-5">
          <p className="font-display text-lg font-bold tracking-tight">{t("createRoom")}</p>
          <div className="mt-3 flex gap-2">
            <Input
              ref={newRoomRef}
              data-testid="room-name-input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="List name: Examples: Work, Friends" className="min-h-[44px] rounded-xl"
            />
            <Button
              data-testid="create-room-button" onClick={createRoom}
              className="min-h-[44px] rounded-full bg-brand px-4 hover:bg-brand-hover active:scale-95"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-5 text-sm font-medium">{t("joinRoom")}</p>
          <div className="mt-2 flex items-center gap-2">
            <div className="flex min-h-[48px] flex-1 items-center gap-2 rounded-full border border-white/10 bg-surfacealt ps-1.5 pe-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-ink">
                <Plus className="h-4 w-4" />
              </span>
              <Input
                data-testid="join-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="Add people or groups"
                className="min-h-[44px] flex-1 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
              />
            </div>
            <Button
              data-testid="join-room-button" variant="outline" onClick={joinRoom}
              className="min-h-[44px] rounded-full px-4 active:scale-95"
            >
              Join
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              data-testid="new-room-mode-standard"
              onClick={() => setNewRoomMode("standard")}
              className={`min-h-[34px] rounded-full px-3 text-xs transition-colors ${
                newRoomMode === "standard" ? "bg-brand text-white" : "bg-surfacealt text-ink-soft"
              }`}
            >
              Standard · translated
            </button>
            <span className="relative inline-flex items-center">
              <select
                data-testid="new-room-ttl"
                value={newRoomTtl}
                onChange={(e) => setNewRoomTtl(Number(e.target.value))}
                className={`min-h-[34px] appearance-none rounded-full border-2 bg-panel ps-3 pe-9 text-xs ring-2 transition-colors ${
                  newRoomTtl
                    ? "border-amber-400 text-amber-300 ring-amber-400/30 shadow-[0_0_14px_rgba(251,191,36,0.45)]"
                    : "border-[#1a7dff] ring-white/80"
                }`}
                title="Self-destruct timer for new messages"
              >
                {TTL_OPTIONS.map((o) => (
                  <option key={o.v} value={o.v}>
                    Self-destruct: {o.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute end-3.5 h-3.5 w-3.5 text-ink-soft" />
            </span>
          </div>
        </div>

        <Dialog open={showMembers} onOpenChange={setShowMembers}>
          <DialogContent className="max-w-sm" data-testid="room-members-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-lg font-bold tracking-tight">
                In “{active?.name}”
              </DialogTitle>
            </DialogHeader>
            <ul className="space-y-2">
              {(active?.member_details || []).map((m) => (
                <li
                  key={m.user_id}
                  data-testid={`member-${m.user_id}`}
                  className="flex items-center gap-3 rounded-2xl border border-white/10 px-3 py-2"
                >
                  <Flag code={m.language} testId={`member-flag-${m.user_id}`} />
                  <span className="min-w-0 flex-1">
                    <span dir="auto" className="flex items-center gap-1.5 truncate text-sm font-medium">
                      {m.name || "Member"} <CrewBadges keys={crew[m.user_id]} />
                      {m.user_id === active?.owner_id && (
                        <span className="ms-2 text-[11px] font-semibold text-brand">owner</span>
                      )}
                      {m.user_id === user?.user_id && (
                        <span className="ms-2 text-[11px] text-ink-soft">you</span>
                      )}
                    </span>
                    <span className="text-xs text-ink-soft">
                      reads in {languages.find((l) => l.code === m.language)?.native || m.language}
                    </span>
                  </span>
                  {active?.owner_id === user?.user_id && m.user_id !== user?.user_id && (
                    <button
                      data-testid={`remove-member-${m.user_id}`}
                      onClick={() => removeMember(m.user_id, m.name)}
                      className="shrink-0 rounded-full border border-white/10 px-3 py-1 text-xs text-ink-soft transition-colors hover:border-destructive/50 hover:text-destructive"
                    >
                      Remove
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-xs text-ink-soft">
              Anyone you remove loses access to this room and its messages straight away.
            </p>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!confirmRoom} onOpenChange={(o) => !o && setConfirmRoom(null)}>
          <AlertDialogContent data-testid="remove-room-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {confirmRoom?.owner_id === user?.user_id
                  ? `Delete “${confirmRoom?.name}”?`
                  : `Leave “${confirmRoom?.name}”?`}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {confirmRoom?.owner_id === user?.user_id
                  ? "You created this room, so it will be deleted for everyone along with its messages. This cannot be undone."
                  : "It disappears from your rooms. You can rejoin later with the invite code."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="remove-room-cancel" className="rounded-full">
                Keep it
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="remove-room-confirm"
                onClick={removeRoom}
                className="rounded-full bg-destructive text-white hover:bg-destructive/90"
              >
                {confirmRoom?.owner_id === user?.user_id ? "Delete room" : "Leave room"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div className="space-y-2" data-testid="room-list">
          <p className="px-2 text-xs uppercase tracking-widest text-ink-soft">{t("yourRooms")}</p>

          <div className="relative px-1">
            <Search className="pointer-events-none absolute start-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-soft" />
            <Input
              data-testid="chat-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search chats and messages"
              className="min-h-[38px] rounded-full ps-9 text-xs"
            />
            {query && (
              <button
                data-testid="chat-search-clear"
                onClick={() => setQuery("")}
                className="absolute end-4 top-1/2 -translate-y-1/2 text-ink-soft hover:text-ink"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-1.5 px-1 pb-1" data-testid="chat-filters">
            {[
              { key: "all", label: "All" },
              { key: "unread", label: `Unread${unreadRooms ? ` ${unreadRooms}` : ""}` },
              { key: "fav", label: "Favorites" },
              { key: "groups", label: "Groups" },
            ].map((chip) => (
              <button
                key={chip.key}
                data-testid={`chat-filter-${chip.key}`}
                onClick={() => setChatFilter(chip.key)}
                className={`min-h-[30px] rounded-full border px-3 text-xs font-semibold transition-colors ${
                  chatFilter === chip.key
                    ? "border-brand bg-brand/20 text-brand"
                    : "border-white/15 text-ink-soft hover:border-brand/40 hover:text-ink"}`}
              >
                {chip.label}
              </button>
            ))}
            <button
              data-testid="chat-filter-more"
              title="Start a new chat"
              onClick={() => {
                setChatFilter("all");
                newRoomRef.current?.focus();
                newRoomRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
              }}
              className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-full border border-white/15 text-ink-soft transition-colors hover:border-brand/40 hover:text-brand"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
            <button
              data-testid="chat-group-icons"
              title="Group my chats by their icon"
              onClick={() => {
                setIconOnly("");
                setByIcon((v) => {
                  localStorage.setItem("cml_chats_by_icon", v ? "0" : "1");
                  return !v;
                });
              }}
              className={`min-h-[30px] rounded-full border px-3 text-xs font-semibold transition-colors ${
                byIcon ? "border-brand bg-brand/20 text-brand"
                       : "border-white/15 text-ink-soft hover:border-brand/40 hover:text-ink"}`}
            >
              By icon
            </button>
          </div>

          {iconOnly && (
            <p className="flex items-center gap-2 px-2 text-[11px] text-ink-soft" data-testid="icon-only-note">
              Showing only {iconOnly}
              <button data-testid="icon-only-clear" onClick={() => setIconOnly("")}
                      className="text-brand underline decoration-dotted">Show all</button>
            </p>
          )}

          {shownRooms.length === 0 && rooms.length > 0 && (
            <p className="px-2 text-xs text-ink-soft" data-testid="chat-filter-empty">
              {chatFilter === "unread" ? "Nothing unread — you're all caught up."
                : chatFilter === "fav" ? "Tap the star on a chat to keep it here."
                : "No group chats yet — a chat with three or more people lands here."}
            </p>
          )}
          {listItems.map((item) => {
            if (item.type === "head") {
              const mark = item.slug === "none" || item.slug === "pinned" || item.slug === "rest"
                ? "" : item.slug;
              return (
                <button
                  key={item.key}
                  data-testid={`chat-group-${item.slug}`}
                  onClick={() => mark && setIconOnly((v) => (v === mark ? "" : mark))}
                  className={`block w-full px-2 pt-2 text-start text-[11px] uppercase tracking-widest transition-colors ${
                    mark ? "hover:text-brand" : ""} ${
                    iconOnly && iconOnly === mark ? "text-brand" : "text-ink-soft"}`}
                >
                  {item.label}
                </button>
              );
            }
            const r = item.r;
            const owner = r.owner_id === user?.user_id;
            return (
              <div
                key={r.room_id}
                className={`group relative rounded-2xl border transition-colors ${
                  active?.room_id === r.room_id ? "border-brand/30 bg-panel" : "border-transparent hover:bg-panel"
                }`}
              >
                <div
                  data-testid={`room-${r.room_id}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => openRoom(r)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openRoom(r)}
                  className="w-full cursor-pointer p-4 pe-10 text-start"
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        data-testid={`room-mark-${r.room_id}`}
                        role="button"
                        tabIndex={0}
                        title="Give this chat a little icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMarking((m) => (m === r.room_id ? "" : r.room_id));
                        }}
                        className={`shrink-0 text-base leading-none transition-opacity ${
                          r.my_emoji ? "" : "opacity-25 group-hover:opacity-70"}`}
                      >
                        {r.my_emoji || <Smile className="h-4 w-4" />}
                      </span>
                      {listRename?.room_id === r.room_id ? (
                        <input
                          data-testid={`rename-room-input-${r.room_id}`}
                          autoFocus
                          value={listRename.name}
                          maxLength={60}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setListRename({ room_id: r.room_id, name: e.target.value })}
                          onBlur={saveListName}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === "Enter") saveListName();
                            if (e.key === "Escape") setListRename(null);
                          }}
                          className="min-w-0 flex-1 rounded-lg border border-brand/50 bg-page px-2 py-0.5 text-sm outline-none"
                        />
                      ) : (
                        <span
                          data-testid={`rename-room-${r.room_id}`}
                          role="button"
                          tabIndex={0}
                          title="Tap the name to rename this chat"
                          dir="auto"
                          onClick={(e) => {
                            e.stopPropagation();
                            setListRename({ room_id: r.room_id, name: r.name });
                          }}
                          className={`truncate rounded px-0.5 transition-colors hover:text-brand ${
                            r.unread && active?.room_id !== r.room_id ? "font-bold text-ink" : "font-medium"}`}
                        >
                          {r.mode === "private" && <Lock className="me-1 inline h-3 w-3 text-brand" />}
                          {r.name}
                        </span>
                      )}
                      {(() => {
                        const codes = [...new Set((r.member_details || []).map((m) => m.language))].slice(0, 6);
                        if (codes.length === 0) return null;
                        return (
                          <span className="flex shrink-0 items-center gap-1" data-testid={`room-flags-${r.room_id}`}>
                            {codes.map((c) => (
                              <Flag key={c} code={c} className="h-[12.6px] w-[18px]" testId={`room-flag-${r.room_id}-${c}`} />
                            ))}
                          </span>
                        );
                      })()}
                    </span>
                    <span className="inline-flex shrink-0 items-center gap-1 text-xs text-ink-soft">
                      {r.unread > 0 && active?.room_id !== r.room_id && (
                        <span
                          data-testid={`unread-badge-${r.room_id}`}
                          aria-label={`${r.unread} unread messages`}
                          className="me-1 inline-flex min-w-[20px] items-center justify-center rounded-full bg-brand px-1.5 py-0.5 text-[11px] font-bold leading-none text-white"
                        >
                          {r.unread > 99 ? "99+" : r.unread}
                        </span>
                      )}
                      {r.ttl_seconds ? <Timer className="h-3 w-3" /> : null}
                      <Users className="h-3 w-3" /> {r.members.length}
                      <span
                        data-testid={`fav-${r.room_id}`}
                        role="button"
                        tabIndex={0}
                        title={r.my_favourite ? "Remove from favorites" : "Keep in favorites"}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavourite(r.room_id, !r.my_favourite);
                        }}
                        className={`ms-1 transition-colors ${
                          r.my_favourite ? "text-amber-300" : "text-ink-soft/40 hover:text-ink-soft"}`}
                      >
                        <Star className={`h-3.5 w-3.5 ${r.my_favourite ? "fill-current" : ""}`} />
                      </span>
                    </span>
                  </span>
                  <span
                    dir="auto"
                    className={`mt-1 block truncate text-xs ${r.unread && active?.room_id !== r.room_id ? "text-ink" : "text-ink-soft"}`}
                  >
                    {r.mode === "private" ? "Encrypted conversation" : r.last_message || "No messages yet"}
                  </span>
                  {marking === r.room_id && (
                    <span
                      data-testid={`emoji-picker-${r.room_id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-2 flex max-h-40 flex-wrap items-center gap-1 overflow-y-auto rounded-xl border border-white/10 bg-page p-2 cml-scroll"
                    >
                      {recentMarks.length > 0 && (
                        <>
                          <span className="w-full text-[10px] uppercase tracking-widest text-ink-soft">
                            Recent
                          </span>
                          {recentMarks.map((m) => (
                            <button
                              key={`recent-${m}`}
                              data-testid={`emoji-recent-${r.room_id}-${m}`}
                              onClick={() => setRoomMark(r.room_id, m)}
                              className={`rounded-lg px-1.5 py-0.5 text-base transition-colors hover:bg-brand/20 ${
                                r.my_emoji === m ? "bg-brand/25" : ""}`}
                            >
                              {m}
                            </button>
                          ))}
                          <span className="my-1 w-full border-t border-white/10" />
                        </>
                      )}
                      {ROOM_MARKS.map((m) => (
                        <button
                          key={m}
                          data-testid={`emoji-pick-${r.room_id}-${m}`}
                          onClick={() => setRoomMark(r.room_id, m)}
                          className={`rounded-lg px-1.5 py-0.5 text-base transition-colors hover:bg-brand/20 ${
                            r.my_emoji === m ? "bg-brand/25" : ""}`}
                        >
                          {m}
                        </button>
                      ))}
                      <button
                        data-testid={`emoji-clear-${r.room_id}`}
                        onClick={() => setRoomMark(r.room_id, "")}
                        className="ms-1 text-[11px] text-ink-soft hover:text-ink"
                      >
                        None
                      </button>
                    </span>
                  )}
                </div>
                {r.live_call && !(call.inCall && active?.room_id === r.room_id) && (
                  <button
                    data-testid={`join-call-${r.room_id}`}
                    onClick={() => {
                      if (active?.room_id !== r.room_id) openRoom(r);
                      setPendingCall(!!r.live_call.video);
                    }}
                    className="mx-4 mb-3 inline-flex items-center gap-1.5 rounded-full bg-brand/15 px-3 py-1 text-xs font-semibold text-brand transition-colors hover:bg-brand/25"
                  >
                    <span className="relative flex h-2 w-2">
                      <span className="absolute h-2 w-2 animate-ping rounded-full bg-brand/70" />
                      <span className="h-2 w-2 rounded-full bg-brand" />
                    </span>
                    Call in progress · Join
                  </button>
                )}
                <button
                  data-testid={`remove-room-${r.room_id}`}
                  title={owner ? "Delete this room for everyone" : "Leave this room"}
                  aria-label={owner ? `Delete ${r.name}` : `Leave ${r.name}`}
                  onClick={() => setConfirmRoom(r)}
                  className="absolute end-2 top-3 rounded-full p-1.5 text-ink-soft/50 opacity-0 transition-all hover:bg-white/5 hover:text-destructive focus:opacity-100 group-hover:opacity-100"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
          {rooms.length === 0 && (
            <p className="px-2 text-sm text-ink-soft">Create your first translated room above.</p>
          )}
          {asked.length >= 2 && hits.messages.length > 0 && (
            <div className="pt-3" data-testid="message-hits">
              <p className="px-2 pb-1 text-[11px] uppercase tracking-widest text-ink-soft">
                In messages · {hits.messages.length}
              </p>
              {hits.messages.slice(0, 8).map((m) => (
                <button
                  key={m.message_id}
                  data-testid={`message-hit-${m.message_id}`}
                  onClick={() => {
                    const room = rooms.find((r) => r.room_id === m.room_id);
                    if (room) openRoom(room);
                    setJumpedId(m.message_id);
                  }}
                  className="block w-full rounded-2xl px-2 py-1.5 text-start transition-colors hover:bg-panel"
                >
                  <span className="block truncate text-xs font-medium">{m.room_name}</span>
                  <span className="block truncate text-[11px] text-ink-soft" dir="auto">
                    {m.sender_name ? `${m.sender_name}: ` : ""}{m.text}
                  </span>
                </button>
              ))}
            </div>
          )}
          {asked.length >= 2 && (hits.notes || []).length > 0 && (
            <div className="pt-3" data-testid="note-hits">
              <p className="px-2 pb-1 text-[11px] uppercase tracking-widest text-ink-soft">
                In my notes · {hits.notes.length}
              </p>
              {hits.notes.slice(0, 8).map((n) => (
                <button
                  key={n.message_id}
                  data-testid={`note-hit-${n.message_id}`}
                  onClick={() => {
                    const room = rooms.find((r) => r.room_id === n.room_id);
                    if (room) openRoom(room);
                    setJumpedId(n.message_id);
                  }}
                  className="block w-full rounded-2xl px-2 py-1.5 text-start transition-colors hover:bg-panel"
                >
                  <span className="block truncate text-xs font-medium">
                    {n.folder ? `${n.folder} · ` : ""}{n.room_name}
                  </span>
                  <span className="block truncate text-[11px] text-brand" dir="auto">{n.note}</span>
                  <span className="block truncate text-[11px] text-ink-soft" dir="auto">{n.text}</span>
                </button>
              ))}
            </div>
          )}
          {asked.length >= 2 && shownRooms.length === 0 && hits.messages.length === 0
            && (hits.notes || []).length === 0 && (
            <p className="px-2 pt-2 text-xs text-ink-soft" data-testid="search-empty">
              Nothing found for “{query.trim()}”.
            </p>
          )}
        </div>
      </aside>

      <section className="relative flex h-[calc((100vh-7rem)*2)] min-h-[1280px] flex-col rounded-3xl border border-white/10 bg-panel lg:col-span-8">
        {active ? (
          <>
            <div className="flex flex-wrap items-center gap-3 border-b border-white/10 p-4">
              <div className="min-w-0">
                {renaming ? (
                  <Input
                    data-testid="room-rename-input"
                    autoFocus
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onBlur={saveName}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveName();
                      if (e.key === "Escape") setRenaming(false);
                    }}
                    className="h-9 w-56 rounded-xl"
                  />
                ) : (
                  <p dir="auto" className="flex items-center gap-2 truncate font-display text-lg font-bold tracking-tight">
                    {active.my_emoji && (
                      <span data-testid="room-header-mark">{active.my_emoji}</span>
                    )}
                    {active.name}
                    {active.owner_id === user?.user_id && (
                      <button
                        data-testid="room-rename-button"
                        title="Rename this room"
                        aria-label="Rename this room"
                        onClick={() => {
                          setNewName(active.name);
                          setRenaming(true);
                        }}
                        className="text-ink-soft/60 transition-colors hover:text-ink"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </p>
                )}
                <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-ink-soft">
                  <span data-testid="room-invite-code" className="inline-flex items-center gap-1">
                    Invite {active.invite_code}
                    <button
                      data-testid="copy-join-link-button"
                      title="Copy one-click join link"
                      onClick={() => {
                        navigator.clipboard?.writeText(
                          `${window.location.origin}/join/${active.invite_code}`
                        );
                        toast.success("One-click join link copied");
                      }}
                      className="inline-flex items-center transition-opacity hover:opacity-70"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  </span>
                  <span>·</span>
                  <button
                    data-testid="room-members-button"
                    onClick={() => setShowMembers(true)}
                    className="inline-flex items-center gap-1 underline decoration-dotted transition-colors hover:text-ink"
                  >
                    <Users className="h-3 w-3" />
                    {(active.member_details || []).length}{" "}
                    {(active.member_details || []).length === 1 ? "person" : "people"}
                  </button>
                </p>
              </div>
              <div className="ms-auto flex flex-wrap items-center gap-2">
                {!call.inCall ? (
                  <>
                    {active.live_call && (
                      <Button
                        data-testid="join-live-call-button" size="sm"
                        className="min-h-[36px] rounded-full bg-brand px-4 hover:bg-brand-hover active:scale-95"
                        onClick={() => startCall(!!active.live_call.video)}
                      >
                        <PhoneCall className="h-3.5 w-3.5" />
                        <span className="ms-1.5">Join call</span>
                      </Button>
                    )}
                    <Button
                      data-testid="start-voice-call-button" variant="outline" size="sm"
                      className="min-h-[36px] rounded-full active:scale-95" onClick={() => startCall(false)}
                    >
                      <Phone className="h-3.5 w-3.5" />
                      <span className="ms-1.5 hidden sm:inline">Call</span>
                    </Button>
                    <Button
                      data-testid="start-video-call-button" variant="outline" size="sm"
                      className="min-h-[36px] rounded-full active:scale-95" onClick={() => startCall(true)}
                    >
                      <Video className="h-3.5 w-3.5" />
                      <span className="ms-1.5 hidden sm:inline">Video</span>
                    </Button>
                  </>
                ) : null}
                {active.owner_id === user?.user_id && (
                  <select
                    data-testid="room-ttl-select"
                    value={active.ttl_seconds || 0}
                    onChange={(e) => changeTtl(e.target.value)}
                    className={`min-h-[36px] rounded-full border bg-panel px-3 text-sm transition-colors ${
                      active.ttl_seconds
                        ? "border-amber-400 text-amber-300 shadow-[0_0_14px_rgba(251,191,36,0.45)]"
                        : "border-white/15"
                    }`}
                    title="Self-destruct timer"
                  >
                    {TTL_OPTIONS.map((o) => (
                      <option key={o.v} value={o.v}>
                        ⏱ {o.label}
                      </option>
                    ))}
                  </select>
                )}
                <LanguageSelect
                  languages={languages}
                  value={roomLang || lang}
                  onChange={changeRoomLanguage}
                  testId="room-language-select"
                  compact
                  disabled={isPrivate}
                />
                <Button
                  data-testid="toggle-original-button" variant="outline" size="sm"
                  className="min-h-[36px] rounded-full active:scale-95"
                  onClick={() => setShowOriginal((v) => !v)}
                >
                  {showOriginal ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  <span className="ms-1.5 hidden sm:inline">Original</span>
                </Button>
                <Button
                  data-testid="learning-tip-button" variant="outline" size="sm"
                  className="min-h-[36px] rounded-full active:scale-95" onClick={getTip}
                  disabled={isPrivate}
                >
                  <Lightbulb className="h-3.5 w-3.5" />
                  <span className="ms-1.5 hidden sm:inline">Tip</span>
                </Button>
                <Button
                  data-testid="transcripts-button" variant="outline" size="sm"
                  className="min-h-[36px] rounded-full active:scale-95"
                  onClick={() => {
                    loadTranscripts(active);
                    setOpenTranscript(!openTranscript);
                  }}
                >
                  <FileText className="h-3.5 w-3.5" />
                  <span className="ms-1.5 hidden sm:inline">Call history ({transcripts.length})</span>
                </Button>
                <Button
                  data-testid="room-listen-board-button" variant="outline" size="sm"
                  className="min-h-[36px] rounded-full active:scale-95"
                  onClick={() => setShowBoard((v) => !v)}
                >
                  <Headphones className="h-3.5 w-3.5" />
                  <span className="ms-1.5 hidden sm:inline">Listening</span>
                </Button>
                <Button
                  data-testid="starred-button" variant="outline" size="sm"
                  className="min-h-[36px] rounded-full active:scale-95"
                  onClick={() => (starList ? setStarList(null) : openStarred())}
                >
                  <Star className="h-3.5 w-3.5" />
                  <span className="ms-1.5 hidden sm:inline">Starred</span>
                </Button>
                <Button
                  data-testid="notes-page-button" variant="outline" size="sm" asChild
                  className="min-h-[36px] rounded-full active:scale-95"
                >
                  <Link to="/notes">
                    <StickyNote className="h-3.5 w-3.5" />
                    <span className="ms-1.5 hidden sm:inline">Notes</span>
                  </Link>
                </Button>
                <Button
                  data-testid="find-in-chat-button" variant="outline" size="sm"
                  className="min-h-[36px] rounded-full active:scale-95"
                  onClick={() => {
                    setFindOpen((v) => !v);
                    setFindTerm("");
                  }}
                >
                  <Search className="h-3.5 w-3.5" />
                  <span className="ms-1.5 hidden sm:inline">Find</span>
                </Button>
              </div>
            </div>

            {showBoard && (
              <div className="border-b border-white/10 px-4 py-3" data-testid="room-listen-board">
                <ListenBoard roomId={active.room_id} onClose={() => setShowBoard(false)} />
              </div>
            )}

            {starList && (
              <div className="border-b border-white/10 px-4 py-3" data-testid="starred-panel">
                <p className="pb-2 text-[11px] uppercase tracking-widest text-ink-soft">
                  Starred messages · {starList.length}
                </p>
                {starList.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 pb-2" data-testid="starred-folders">
                    {[{ name: "", count: starList.length, label: "All" },
                      ...starFolders.map((f) => ({ ...f, label: f.name })),
                    ].map((f) => (
                      <button
                        key={f.name || "all"}
                        data-testid={`starred-folder-${f.name || "all"}`}
                        onClick={() => setStarFolder(f.name)}
                        className={`min-h-[26px] rounded-full border px-2.5 text-[11px] font-semibold transition-colors ${
                          starFolder === f.name
                            ? "border-brand bg-brand/20 text-brand"
                            : "border-white/15 text-ink-soft hover:text-ink"}`}
                      >
                        {f.label} {f.count}
                      </button>
                    ))}
                  </div>
                )}
                <datalist id="starred-folder-names">
                  {["Phrases", "Plans", "Links",
                    ...starFolders.map((f) => f.name)].filter((n, i, all) => all.indexOf(n) === i)
                    .map((n) => <option key={n} value={n} />)}
                </datalist>
                {starList.length === 0 ? (
                  <p className="text-xs text-ink-soft" data-testid="starred-empty">
                    Tap the star on a message and it will be kept here.
                  </p>
                ) : (
                  <div className="max-h-52 space-y-1 overflow-y-auto cml-scroll">
                    {starList.filter((s) => (
                      starFolder ? (s.folder || "") === starFolder : true)).map((s) => (
                      <div key={s.message_id}>
                        <button
                          data-testid={`starred-row-${s.message_id}`}
                          onClick={() => {
                            const room = rooms.find((r) => r.room_id === s.room_id);
                            if (room && room.room_id !== active.room_id) openRoom(room);
                            setJumpedId(s.message_id);
                          }}
                          className="block w-full rounded-xl px-2 py-1.5 text-start transition-colors hover:bg-page"
                        >
                          <span className="block truncate text-[11px] text-ink-soft">
                            {s.room_name}{s.sender_name ? ` · ${s.sender_name}` : ""}
                          </span>
                          <span className="block truncate text-xs" dir="auto">{s.text}</span>
                        </button>
                        <div className="flex flex-wrap items-center gap-1.5 px-2 pb-1">
                          <input
                            data-testid={`starred-folder-input-${s.message_id}`}
                            list="starred-folder-names"
                            defaultValue={s.folder || ""}
                            placeholder="Folder"
                            onBlur={(e) => {
                              const name = e.target.value.trim();
                              if (name !== (s.folder || "")) fileStarred(s.message_id, name);
                            }}
                            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                            className="min-h-[26px] w-28 rounded-full border border-white/15 bg-page px-2.5 text-[11px] outline-none focus:border-brand/50"
                          />
                          <input
                            data-testid={`starred-note-input-${s.message_id}`}
                            defaultValue={s.note || ""}
                            placeholder="Why I kept it"
                            onBlur={(e) => {
                              const note = e.target.value.trim();
                              if (note !== (s.note || "")) noteStarred(s.message_id, note);
                            }}
                            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                            className="min-h-[26px] w-44 rounded-full border border-white/15 bg-page px-2.5 text-[11px] outline-none focus:border-brand/50"
                          />
                          {passOn === s.message_id ? (
                            <>
                              <select
                                data-testid={`pass-lang-${s.message_id}`}
                                value={passLang}
                                onChange={(e) => setPassLang(e.target.value)}
                                className="min-h-[28px] rounded-full border border-white/15 bg-page px-2 text-[11px]"
                              >
                                {languages.map((l) => (
                                  <option key={l.code} value={l.code}>{l.name}</option>
                                ))}
                              </select>
                              <select
                                data-testid={`pass-room-${s.message_id}`}
                                value={passRoom}
                                onChange={(e) => setPassRoom(e.target.value)}
                                className="min-h-[28px] rounded-full border border-white/15 bg-page px-2 text-[11px]"
                              >
                                <option value="">Just give me the words</option>
                                {rooms.map((r) => (
                                  <option key={r.room_id} value={r.room_id}>Send to {r.name}</option>
                                ))}
                              </select>
                              <button
                                data-testid={`pass-go-${s.message_id}`}
                                onClick={() => passItOn(s)}
                                disabled={passing}
                                className="inline-flex min-h-[28px] items-center gap-1 rounded-full bg-brand px-2.5 text-[11px] font-semibold text-white disabled:opacity-50"
                              >
                                {passing ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : <Send className="h-3 w-3" />} Go
                              </button>
                              <button data-testid={`pass-cancel-${s.message_id}`}
                                      onClick={() => setPassOn("")}
                                      className="text-[11px] text-ink-soft hover:text-ink">Cancel</button>
                            </>
                          ) : (
                            <button
                              data-testid={`pass-open-${s.message_id}`}
                              onClick={() => {
                                setPassOn(s.message_id);
                                setPassLang(roomLang || "en");
                                setPassRoom("");
                              }}
                              className="text-[11px] text-brand underline decoration-dotted"
                            >
                              Send it on in another language
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {findOpen && (
              <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2"
                   data-testid="find-in-chat-bar">
                <Search className="h-3.5 w-3.5 shrink-0 text-ink-soft" />
                <input
                  data-testid="find-in-chat-input"
                  autoFocus
                  value={findTerm}
                  onChange={(e) => setFindTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") hopFind(e.shiftKey ? -1 : 1);
                    if (e.key === "Escape") setFindOpen(false);
                  }}
                  placeholder="Find in this chat"
                  className="min-w-0 flex-1 bg-transparent text-xs outline-none"
                />
                <span className="shrink-0 text-[11px] text-ink-soft" data-testid="find-in-chat-count">
                  {findTerm.trim().length < 2 ? "" : found.length === 0 ? "No matches"
                    : `${findAt + 1} of ${found.length}`}
                </span>
                <button data-testid="find-in-chat-prev" onClick={() => hopFind(-1)}
                        disabled={found.length === 0}
                        className="rounded-full p-1 text-ink-soft transition-colors hover:text-brand disabled:opacity-40">
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <button data-testid="find-in-chat-next" onClick={() => hopFind(1)}
                        disabled={found.length === 0}
                        className="rounded-full p-1 text-ink-soft transition-colors hover:text-brand disabled:opacity-40">
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                <button data-testid="find-in-chat-close" onClick={() => setFindOpen(false)}
                        className="rounded-full p-1 text-ink-soft transition-colors hover:text-ink">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            {openTranscript && (
              <CallHistory
                calls={transcripts}
                onJoinLive={(c) => {
                  setOpenTranscript(null);
                  startCall(!!c.video);
                }}
              />
            )}

            {(callOpen || call.inCall) && (
              <CallPanel
                call={call}
                onSavePhrase={savePhrase}
                onClose={() => {
                  setCallOpen(false);
                  loadTranscripts(active);
                }}
              />
            )}

            {isPrivate && (
              <div
                className="flex flex-wrap items-center gap-2 border-b border-white/10 bg-brand/10 px-5 py-3 text-sm"
                data-testid="private-room-banner"
              >
                <Lock className="h-4 w-4 text-brand" />
                <span>
                  End-to-end encrypted — translation is off in this room. Share the passphrase with members
                  outside ChatMiLu.
                </span>
                <div className="ms-auto flex items-center gap-2">
                  <Input
                    data-testid="room-passphrase-input"
                    type="password"
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                    placeholder={locked ? "Enter passphrase" : "Change passphrase"}
                    className="h-9 w-40 rounded-full"
                  />
                  <Button
                    data-testid="unlock-room-button" size="sm" onClick={unlock}
                    className="min-h-[36px] rounded-full bg-brand px-4 hover:bg-brand-hover active:scale-95"
                  >
                    <KeyRound className="h-3.5 w-3.5" />
                    <span className="ms-1.5">{locked ? "Unlock" : "Set"}</span>
                  </Button>
                </div>
              </div>
            )}

            {tip && (
              <p data-testid="learning-tip" dir="auto" className="border-b border-white/10 bg-forest/10 px-5 py-3 text-sm">
                {tip}
              </p>
            )}

            {pins.length > 0 && (
              <div className="border-b border-white/10 bg-brand/5 px-5 py-3" data-testid="pinned-bar">
                <p className="text-xs uppercase tracking-widest text-ink-soft">Pinned</p>
                <ul className="mt-2 space-y-1.5">
                  {pins.map((p) => (
                    <li
                      key={p.pin_id}
                      data-testid={`pin-${p.pin_id}`}
                      className="flex items-start justify-between gap-3 text-sm"
                    >
                      <span dir="auto" className="min-w-0">
                        <Pin className="me-1.5 inline h-3 w-3 text-brand" />
                        {p.display_text}
                        <span className="ms-2 text-xs text-ink-soft">· {p.pinned_by}</span>
                      </span>
                      {active.owner_id === user?.user_id && (
                        <button data-testid={`unpin-${p.pin_id}`} onClick={() => unpin(p.pin_id)}>
                          <PinOff className="h-3.5 w-3.5 text-ink-soft hover:text-destructive" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {active.owner_id === user?.user_id && (
              <div className="flex flex-wrap gap-2 border-b border-white/10 px-5 py-3" data-testid="owner-tools">
                <Input
                  data-testid="pin-note-input"
                  value={pinNote}
                  onChange={(e) => setPinNote(e.target.value)}
                  placeholder="Pin a note for everyone (auto-translated)"
                  className="h-10 flex-1 rounded-full"
                />
                <Button
                  data-testid="pin-note-button"
                  size="sm"
                  onClick={() => pinNote.trim() && pinMessage({ note: pinNote.trim() })}
                  className="min-h-[40px] rounded-full bg-brand px-4 hover:bg-brand-hover active:scale-95"
                >
                  <Pin className="h-3.5 w-3.5" />
                  <span className="ms-1.5 hidden sm:inline">Pin note</span>
                </Button>
                <RoomInvites roomId={active.room_id} emailReady={emailReady} />
              </div>
            )}

            <div
              ref={scrollBoxRef}
              onScroll={() => atBottom() && setUnseen(0)}
              className="flex-1 space-y-4 overflow-y-auto p-5 cml-scroll"
              data-testid="room-messages"
            >
              {messages.map((m, i) => {
                const mine = m.sender_id === user?.user_id;
                const prev = messages[i - 1];
                const newDay =
                  m.created_at &&
                  (!prev?.created_at ||
                    new Date(prev.created_at).toDateString() !== new Date(m.created_at).toDateString());
                return (
                  <div key={m.message_id}>
                    {newDay && (
                      <p
                        data-testid={`day-divider-${i}`}
                        className="my-4 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-widest text-ink-soft/60"
                      >
                        <span className="h-px flex-1 bg-white/10" />
                        {dayLabel(m.created_at)}
                        <span className="h-px flex-1 bg-white/10" />
                      </p>
                    )}
                  <div className={`flex ${mine ? "justify-end" : "justify-start"}`}
                       id={`msg-${m.message_id}`}
                       data-testid={`room-message-${i}`}>
                    <div
                      className={`max-w-[88%] break-words rounded-2xl px-3.5 py-2 text-[13px] sm:max-w-[58%] transition-shadow ${
                        mine ? "rounded-br-sm bg-brand text-white" : "rounded-bl-sm border border-white/10 bg-page"
                      } ${jumpedId === m.message_id ? "ring-2 ring-amber-300 ring-offset-2 ring-offset-panel" : ""}`}
                    >
                      <p className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-semibold ${mine ? "text-white/80" : "text-ink-soft"}`}>
                        <span className="truncate">{m.sender_name} · {m.source_language}</span>
                        <CrewBadges keys={crew[m.sender_id]} />
                        {m.kind === "voice" && <Mic className="h-3 w-3" />}
                        {m.expires_at && <Timer className="h-3 w-3" />}
                        {m.encrypted ? (
                          <Lock className="h-3 w-3" />
                        ) : (
                          <>
                            <button
                              data-testid={`play-message-${i}`}
                              onClick={() => playMessage(m)}
                              title="Listen in my language"
                              className="inline-flex items-center transition-opacity hover:opacity-70"
                            >
                              {playingId === m.message_id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Volume2 className="h-3.5 w-3.5" />
                              )}
                            </button>
                            <button
                              data-testid={`save-phrase-${i}`}
                              onClick={() => savePhrase(m.original_text, m.source_language)}
                              title="Save to my phrasebook"
                              className="inline-flex items-center transition-opacity hover:opacity-70"
                            >
                              <BookmarkPlus className="h-3.5 w-3.5" />
                            </button>
                            <button
                              data-testid={`star-message-${i}`}
                              onClick={() => toggleStar(m)}
                              title={starred.has(m.message_id) ? "Remove star" : "Star this message"}
                              className="inline-flex items-center transition-opacity hover:opacity-70"
                            >
                              <Star className={`h-3.5 w-3.5 ${
                                starred.has(m.message_id) ? "fill-amber-300 text-amber-300" : ""}`} />
                            </button>
                            <button
                              data-testid={`voice-reply-${i}`}
                              onClick={() => setReplyTo(m)}
                              title="Reply by voice"
                              className="inline-flex items-center transition-opacity hover:opacity-70"
                            >
                              <Reply className="h-3.5 w-3.5" />
                            </button>
                            {!mine && m.kind === "cheer" && (
                              <button
                                data-testid={`cheer-back-${i}`}
                                onClick={() => cheerBack(m)}
                                title="Cheer them back"
                                className="inline-flex items-center gap-1 transition-opacity hover:opacity-70"
                              >
                                <ThumbsUp className="h-3.5 w-3.5" /> Cheer back
                              </button>
                            )}
                            {mine && m.kind === "text" && (
                              <button
                                data-testid={`edit-message-${i}`}
                                onClick={() => {
                                  setEditingId(m.message_id);
                                  setEditText(m.original_text);
                                }}
                                title="Edit this message"
                                className="inline-flex items-center transition-opacity hover:opacity-70"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {mine && (
                              <button
                                data-testid={`delete-message-${i}`}
                                onClick={() => deleteMessage(m)}
                                title="Delete this message"
                                className="inline-flex items-center transition-opacity hover:opacity-70"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {active.owner_id === user?.user_id && (
                              <button
                                data-testid={`pin-message-${i}`}
                                onClick={() => pinMessage({ message_id: m.message_id })}
                                title="Pin this message"
                                className="inline-flex items-center transition-opacity hover:opacity-70"
                              >
                                <Pin className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </>
                        )}
                      </p>
                      {m.reply_to_text && (
                        <p
                          dir="auto"
                          className={`mt-1.5 rounded-xl border-s-2 px-2 py-1 text-xs ${
                            mine ? "border-white/50 bg-white/10 text-white/80" : "border-brand bg-white/5 text-ink-soft"
                          }`}
                        >
                          ↩ {m.reply_to_name}: {m.reply_to_text}
                        </p>
                      )}
                      {editingId === m.message_id ? (
                        <span className="mt-1.5 flex items-center gap-2">
                          <Input
                            data-testid={`edit-input-${i}`}
                            autoFocus
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveEdit(m);
                              if (e.key === "Escape") setEditingId(null);
                            }}
                            className="h-8 flex-1 rounded-xl bg-page text-[13px] text-ink"
                          />
                          <button
                            data-testid={`edit-save-${i}`}
                            onClick={() => saveEdit(m)}
                            className="text-[11px] font-semibold underline"
                          >
                            Save
                          </button>
                          <button
                            data-testid={`edit-cancel-${i}`}
                            onClick={() => setEditingId(null)}
                            className="text-[11px] opacity-70"
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                      <p dir="auto" className="mt-1 whitespace-pre-wrap leading-snug">
                        <RichText text={m.display_text} />
                        {m.edited_at && (
                          <span
                            data-testid={`edited-tag-${i}`}
                            className={`ms-1.5 text-[10px] ${mine ? "text-white/60" : "text-ink-soft/70"}`}
                          >
                            (edited)
                          </span>
                        )}
                        {m.created_at && (
                          <span
                            data-testid={`message-time-${i}`}
                            className={`ms-2 whitespace-nowrap align-baseline text-[10px] tabular-nums ${
                              mine ? "text-white/60" : "text-ink-soft/70"
                            }`}
                          >
                            {new Date(m.created_at).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        )}
                      </p>
                      )}
                      {!m.encrypted && <LinkPreview text={m.display_text} mine={mine} />}
                      {showOriginal && !m.encrypted && m.display_text !== m.original_text && (
                        <p
                          dir="auto"
                          className={`mt-2 border-t border-dashed pt-2 text-xs ${
                            mine ? "border-white/30 text-white/80" : "border-white/15 text-ink-soft"
                          }`}
                        >
                          {m.original_text}
                        </p>
                      )}
                      {mine && i === messages.length - 1 && reads.length > 0 && (
                        <p
                          data-testid="read-receipt"
                          className="mt-2 inline-flex items-center gap-1 text-[11px] text-white/70"
                        >
                          <Check className="h-3 w-3" />
                          Seen by{" "}
                          {reads
                            .filter((r) => !m.created_at || (r.last_read_at || "") >= m.created_at)
                            .map((r) => r.name || "member")
                            .join(", ") || "no one yet"}
                        </p>
                      )}
                    </div>
                  </div>
                  </div>
                );
              })}
              {messages.length === 0 && (
                <p className="py-16 text-center text-sm text-ink-soft">
                  Write in your language — everyone reads it in theirs.
                </p>
              )}
              <div ref={endRef} />
            </div>
            {unseen > 0 && (
              <button
                data-testid="new-messages-pill"
                onClick={jumpToLatest}
                className="rise fixed bottom-28 left-1/2 z-30 -translate-x-1/2 rounded-full bg-brand px-4 py-2 text-xs font-semibold text-white shadow-[0_10px_30px_rgba(26,125,255,0.45)] transition-colors hover:bg-brand-hover sm:bottom-32"
              >
                {unseen} new {unseen === 1 ? "message" : "messages"} ↓
              </button>
            )}

            {away && unseen === 0 && (
              <button
                data-testid="jump-to-latest"
                onClick={jumpToLatest}
                title="Back to the newest message"
                className="fixed bottom-28 end-8 z-30 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-panel/95 text-ink-soft shadow-xl backdrop-blur transition-colors hover:border-brand/50 hover:text-brand sm:bottom-32"
              >
                <ArrowDown className="h-4 w-4" />
              </button>
            )}

            {replyTo && (
              <p
                className="flex items-center gap-2 border-t border-white/10 px-5 pt-3 text-xs text-ink-soft"
                data-testid="reply-context"
              >
                <Reply className="h-3.5 w-3.5 text-brand" />
                Voice reply to {replyTo.sender_name}: “{(replyTo.original_text || "").slice(0, 60)}”
                <button data-testid="cancel-reply" onClick={() => setReplyTo(null)}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </p>
            )}

            {typingNames.length > 0 && (
              <p
                data-testid="typing-indicator"
                className="flex items-center gap-2 px-5 pb-2 text-xs text-brand"
              >
                <span className="typing-dots" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>
                {typingNames.join(", ")} {typingNames.length === 1 ? "is" : "are"} typing…
              </p>
            )}

            <div className="sticky bottom-0 z-20 flex items-center gap-3 rounded-b-3xl border-t border-white/10 bg-panel p-4">
              <Input
                data-testid="room-message-input" dir="auto" value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  notifyTyping();
                }}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={isPrivate ? "Encrypted message…" : t("typeMessage")} className="min-h-[48px] flex-1 rounded-2xl"
              />
              <Button
                data-testid="autoplay-toggle"
                variant="outline"
                onClick={() => {
                  const next = !autoPlay;
                  setAutoPlay(next);
                  localStorage.setItem("cml_autoplay", next ? "on" : "off");
                  toast.success(next ? "Voice messages will auto-play" : "Auto-play off");
                }}
                title="Auto-play incoming voice messages in my language"
                className="min-h-[48px] rounded-full px-4 active:scale-95"
              >
                {autoPlay ? <Volume1 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              </Button>
              <Button
                data-testid="voice-record-button"
                onClick={recording ? stopRecording : startRecording}
                disabled={uploading || isPrivate}
                title="Record a voice message — it will be transcribed, translated and spoken in each language"
                className={`min-h-[48px] rounded-full px-5 active:scale-95 ${
                  recording ? "recording bg-destructive hover:bg-destructive/90" : "bg-white/10 hover:bg-white/20"
                }`}
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : recording ? (
                  <Square className="h-4 w-4" />
                ) : (
                  <Mic className="h-4 w-4" />
                )}
              </Button>
              <Button
                data-testid="room-send-button" onClick={send} disabled={sending || !text.trim()}
                className="min-h-[48px] rounded-full bg-forest px-5 text-page hover:bg-forest/90 active:scale-95"
              >
                <Send className="h-4 w-4 rtl:rotate-180" />
                <span className="ms-2 hidden sm:inline">{t("send")}</span>
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center p-10 text-center">
            <Users className="h-8 w-8 text-forest" />
            <p className="mt-4 font-display text-xl font-bold tracking-tight">No room selected</p>
            <p className="mt-1 max-w-sm text-sm text-ink-soft">
              Create a room or join one with an invite code to start translated messaging.
            </p>
          </div>
        )}
      </section>

      <PageGuide name="messages" />
    </div>
  );
}
